import { Button } from "components/Button";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router";
import { iconArrowRight } from "assets/icons";
import { FilterProps } from "components/TableModified/TableModifiedFilters";
import { CheckBox } from "components/CheckBox";
import { useGraphQLAuth } from "hooks/useGraphQLAuth";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface Property {
  name?: string,
  value?: string,
  action?: () => void,
}
interface Event {
  eventId: string,
  name: string,
  description?: string,
  organizer?: string,
  address: string,
  maximumCapacity: number,
  isPublished: boolean,
  dateDescription: string,
  dateComplement: string,
  insertDate: string,
  updateDate: string,
  adminsCount: number,
  registrationsApprovedCount: number
}

interface EventRowProps {
  name: string,
  organizer: string,
  address: string,
  maximumCapacity: number,
  isPublished: JSX.Element | string,
  dateDescription: string,
  actions: JSX.Element[],
}

export const useEventsTable = () => {
  const initialTableFilters: FilterProps = {
    // Remove isPublished filter as it's not supported by the backend schema
  };
  
  const [properties, setProperties] = useState<Property[]>();
  const [events, setEvents] = useState<Event[]>();
  const [eventRows, setEventRows] = useState<EventRowProps[]>([]);
  const [tableFilters, setTableFilters] = useState<FilterProps>(initialTableFilters);
  const [searchText, setSearchText] = useState<string>('');
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [eventsPerPage, setEventsPerPage] = useState<number>(10);
  const [eventsCount, setEventsCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const searchTextFormRef = useRef<any>(null);
  const { push } = useHistory();
  const { connectGraphQLApi, cancelRequest } = useGraphQLAuth();

  /* HOOKS */
  useEffect(() => {
    return () => {
      cancelRequest();
    }
  }, []);
  
  useEffect(() => {
    setPageNumber(1);
  }, [tableFilters, searchText, eventsPerPage])
  
  useEffect(() => {
    getEvents();
  }, [tableFilters, searchText, pageNumber, eventsPerPage])

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return format(date, "dd/MM/yyyy", { locale: ptBR });
  };

  const booleanBadge = (field: boolean, label: string) => {
    return (
      <CheckBox
        label={label}
        name="isPublished"
        value=""
        readOnly={true}
        checked={true}
        checkedError={!field}
      />
    )
  }

  const rowActionButton = (route: string) => {
    return (
      <Button 
        label="" 
        size="small" 
        icon={iconArrowRight} 
        onlyIcon={true} 
        onClick={() => push(route)} 
      />
    )
  };

  /* EVENT HANDLERS */
  const handleSearchTextFormSubmit = (event: any) => {
    event.preventDefault();
    const text = event.target.searchText.value;
    setSearchText(text);
  }

  const handleFilterChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const eventName = event.target.name;
    const eventValue = event.target.value;
    setTableFilters({
      ...tableFilters,
      [eventName]: {
        ...tableFilters[eventName],
        value: eventValue,
      }
    })
  }

  const handleFilterRemove = (filterName: string): void => {
    setTableFilters({
      ...tableFilters,
      [filterName]: {
        ...tableFilters[filterName],
        value: '',
      }
    })
  }

  const handleSearchTextRemove = (): void => {
    searchTextFormRef.current.reset();
    setSearchText('');
  }

  const handlePageSelected = (page: number): void => {
    setPageNumber(page);
  }

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const eventValue = event.target.value;
    setEventsPerPage(parseInt(eventValue));
  }

  /* API CALLS */
  const getEvents = async () => {
    setLoading(true);
    cancelRequest();
    
    const query = `
      query GetBackofficeEvents($filter: EventFilterInput, $pagination: PaginationInput) {
        backofficeEvents(filter: $filter, pagination: $pagination) {
          eventId
          name
          organizer
          address
          maximumCapacity
          isPublished
          dateDescription
          dateComplement
          adminsCount
          registrationsApprovedCount
        }
        backofficeEventsCount(filter: $filter)
      }
    `;

    const variables = {
      filter: {
        text: searchText || undefined,
      },
      pagination: {
        offset: (pageNumber - 1) * eventsPerPage,
        limit: eventsPerPage,
        sortBy: undefined
      }
    };

    try {
      const response = await connectGraphQLApi({
        query,
        variables,
        errorMessage: 'Falha ao listar eventos',
        useNewApi: true
      });

      setError(false);
      const responseData = (response as any).data;
      const events: Event[] = responseData.backofficeEvents || [];
      const eventsCount: number = responseData.backofficeEventsCount || 0;
      
      // Create properties based on the events data
      const publishedEvents = events.filter(e => e.isPublished).length;
      const totalCapacity = events.reduce((sum, e) => sum + e.maximumCapacity, 0);
      
      const properties: Property[] = [
        { name: 'Total de Eventos', value: eventsCount.toString() },
        { name: 'Eventos Publicados', value: publishedEvents.toString() },
        { name: 'Capacidade Total', value: totalCapacity.toString() }
      ];

      setProperties(properties);
      setEvents(events);
      setEventsCount(eventsCount);
      setEventRows([
        ...events.map((event: Event) => ({
          name: event.name,
          organizer: event.organizer || '-',
          address: event.address,
          maximumCapacity: event.maximumCapacity,
          isPublished: booleanBadge(event.isPublished, event.isPublished ? "Publicado" : "Não Publicado"),
          dateDescription: event.dateDescription,
          actions: [rowActionButton(`/events/${event.eventId}`)], // Future route for event details
        })),
      ]);
      setLoading(false);
    } catch (error: any) {
      console.error('Error fetching events:', error);
      if (error?.errorType === 'connection') {
        setError(true);
        setLoading(false);
      }
    }
  }

  return {
    loading,
    error,
    properties,
    eventRows,
    eventsCount,
    searchText,
    tableFilters,
    searchTextFormRef,
    pageNumber,
    eventsPerPage,
    handleSearchTextFormSubmit,
    handleFilterChange,
    handleFilterRemove,
    handleSearchTextRemove,
    handlePageSelected,
    handleRowsPerPageChange,
  };
} 